import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, Input } from '@plurall/elo-beta'

import { graphqlClient, handleGraphQLErrors } from 'utils'
import { CHANGE_PASSWORD, CHANGE_USER_STATUS, LOGIN } from 'graphql/mutations'
import { ValidatePasswordStrength } from 'App/components/PasswordStrength'

import { renderToastSuccess, renderValidationText } from '../../utils/functions'
import { trackingEventNames } from 'utils/trackings'
import { MAXIMUM_PASSWORD_LENGTH, MESSAGES, MINIMUM_PASSWORD_LENGTH } from '../../utils/constants'

const DialogPassword = ({ username, onClose, userId }) => {
  const [error, setError] = useState(null)
  const [errorNewPassword, setErrorNewPassword] = useState(null)
  const [errorCurrentPassword, setErrorCurrentPassword] = useState(null)
  const [passwordStrength, setPasswordStrength] = useState(null)
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [loadingChange, setLoadingChange] = useState(false)
  const [loadingChangeUserStatus, setLoadingChangeUserStatus] = useState(false)
  const [loadingCheck, setLoadingCheck] = useState(false)

  const fireTracking = status => {
    window.PLURALL_TRACKER.track(trackingEventNames.AccessData.student.responsePasssword, {
      status,
    })
  }

  const checkCurrentPassword = async () => {
    try {
      setLoadingCheck(true)

      await graphqlClient().mutate({
        mutation: LOGIN,
        variables: { username, password: oldPassword },
      })
      setErrorCurrentPassword(null)
      changeUserPassword()
      fireTracking('success')
    } catch (error) {
      if (error.graphQLErrors) {
        fireTracking(error.graphQLErrors[0].message)
        setErrorCurrentPassword(error.graphQLErrors[0].message)
        return
      }

      setErrorCurrentPassword('Não foi possível alterar a senha. Por favor, tente novamente.')
    } finally {
      setLoadingCheck(false)
    }
  }

  const checkNewPassword = () => {
    if (newPassword !== confirmNewPassword) {
      setError(MESSAGES.different_password)
    } else {
      setError(null)
      checkCurrentPassword()
    }
  }

  const checkPasswordStrength = async () => {
    const strength = await ValidatePasswordStrength(newPassword)
    setPasswordStrength(strength)
  }

  const changeUserPassword = async () => {
    try {
      setLoadingChange(true)
      setLoadingChangeUserStatus(true)

      await graphqlClient().mutate({
        mutation: CHANGE_PASSWORD,
        variables: { username, oldPassword, newPassword },
      })

      await graphqlClient().mutate({
        mutation: CHANGE_USER_STATUS,
        variables: { id: +userId },
      })

      onClose()
      renderToastSuccess()
    } catch (error) {
      if (error.graphQLErrors) {
        setErrorNewPassword(handleGraphQLErrors(error.graphQLErrors))
      }
    } finally {
      setLoadingChange(false)
      setLoadingChangeUserStatus(false)
    }
  }

  const isDisabled =
    !oldPassword || newPassword.length < MINIMUM_PASSWORD_LENGTH || !confirmNewPassword

  return (
    <Dialog
      title='Alteração de senha'
      primaryButtonText='Confirmar alteração'
      onClose={onClose}
      onPrimaryClick={() => {
        window.PLURALL_TRACKER.track(trackingEventNames.AccessData.student.savePasssword)

        checkNewPassword()
      }}
      primaryButtonLoading={loadingChange || loadingChangeUserStatus || loadingCheck}
      closePrimaryClick={false}
      primaryButtonDisabled={isDisabled}
    >
      <Input
        placeholder='Senha atual'
        type='password'
        onChange={e => setOldPassword(e)}
        dataTestId='input-oldPassword'
        {...renderValidationText(errorCurrentPassword, 'negative', errorCurrentPassword)}
      />
      <Input
        placeholder='Nova senha'
        type='password'
        hintText='Mínimo de 8 caracteres (Combine letras e números).'
        dataTestId='input-newPassword'
        {...renderValidationText(
          passwordStrength,
          passwordStrength?.variant,
          passwordStrength?.text,
        )}
        {...renderValidationText(errorNewPassword, 'negative', errorNewPassword)}
        onChange={e => setNewPassword(e)}
        onBlur={checkPasswordStrength}
        maxLength={MAXIMUM_PASSWORD_LENGTH}
      />
      <Input
        placeholder='Repita a nova senha'
        type='password'
        onChange={e => setConfirmNewPassword(e)}
        dataTestId='input-confirmPassword'
        {...renderValidationText(error, 'negative', error)}
        maxLength={MAXIMUM_PASSWORD_LENGTH}
      />
    </Dialog>
  )
}

DialogPassword.propTypes = {
  onClose: PropTypes.func.isRequired,
  username: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
}

export default DialogPassword
